"use client";

import { FC, HTMLAttributes, useEffect, useRef, useState } from "react";
import { clsx } from "clsx";

import HtmlText from "../common/HtmlText";
import BlogAuthorCardSection, {
  IAuthorDetails,
  ISocialHandle,
} from "./BlogAuthorCardSection";

import CKEditorHtmlContent from "@/sections/common/CKEditorHtmlContent";
import TwoColumnTab, { ITwoColumnTab } from "./TwoColumnTab";
import BlogSocialMediaHandles from "./BlogSocialMediaHandles";
import BlogImageLightbox from "./BlogImageLightbox";

// react-scroll relied on each section being a structured BlogDetailSection
// with its own outer <h2>. Older blogs are authored as a single CKEditor
// blob with inline <h2>s, so the static TOC prop went empty on them. We
// now extract headings from the rendered article DOM instead — works for
// both authoring styles uniformly. IntersectionObserver drives the active
// highlight; native scrollTo handles smooth navigation with header offset.

// Stable id derivation. URL-friendly slug, falls back to a numeric suffix
// if two headings collide.
const slugify = (text: string) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-");

// -----------------------------------------------------------------------------------------

export type IBlogDetailHtmlTextProps = HTMLAttributes<HTMLElement> & {
  details: {
    heading?: string;
    description?: string;
    tabs?: ITwoColumnTab[];
  }[];
  socialHandles?: ISocialHandle[];
  author: IAuthorDetails;
  tableOfContents?: (string | undefined)[];
  pageName?: "leadership" | "technical";
};

const BlogDetailHtmlText: FC<IBlogDetailHtmlTextProps> = (props) => {
  const {
    details,
    socialHandles,
    tableOfContents,
    author,
    pageName,
    className,
    ...other
  } = props;

  // Auto-derive TOC from h2 elements inside the article (after mount).
  // Works regardless of whether each detail has its own heading or all
  // content is in one CKEditor body with inline h2s.
  const articleRef = useRef<HTMLElement>(null);
  const [autoToc, setAutoToc] = useState<{ id: string; text: string }[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  useEffect(() => {
    if (!articleRef.current) return;

    const headings = Array.from(
      articleRef.current.querySelectorAll<HTMLHeadingElement>("h2"),
    );

    const seen = new Set<string>();
    const items = headings
      .map((h) => {
        const text = (h.textContent ?? "").trim();
        if (!text) return null;
        let id = h.id || slugify(text);
        let i = 1;
        while (seen.has(id)) {
          id = `${slugify(text)}-${++i}`;
        }
        seen.add(id);
        if (h.id !== id) h.id = id;
        return { id, text };
      })
      .filter((x): x is { id: string; text: string } => x !== null);

    setAutoToc(items);

    if (items.length === 0) return;

    // Active highlight via IntersectionObserver. rootMargin offsets so
    // a heading is "active" once it enters the top third of the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the topmost intersecting entry as active.
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              a.boundingClientRect.top - b.boundingClientRect.top,
          );
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-96px 0px -60% 0px", threshold: [0, 1] },
    );
    headings.forEach((h) => observer.observe(h));
    return () => observer.disconnect();
  }, [details]);

  const scrollToHeading = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const y = el.getBoundingClientRect().top + window.scrollY - 96;
    window.scrollTo({ top: y, behavior: "smooth" });
  };

  // Lightbox state — clicking any <img> in the article opens it enlarged.
  const [lightboxImage, setLightboxImage] = useState<
    { src: string; alt: string } | null
  >(null);

  // Code-block enhancer — runs after hydration, decorates every <pre>
  // in the article with a header bar (language label + copy button)
  // and a dark editor-style theme. Works for any CKEditor output that
  // wraps source in <pre><code class="language-xxx">…</code></pre>
  // (the standard hljs/Prism convention CKEditor's code-block plugin
  // emits) and falls back gracefully if the language class is missing.
  useEffect(() => {
    if (!articleRef.current) return;
    const pres = Array.from(
      articleRef.current.querySelectorAll<HTMLPreElement>("pre"),
    );
    const disposers: Array<() => void> = [];

    pres.forEach((pre) => {
      // Skip if already enhanced (e.g., StrictMode double-invoke or
      // re-render where the DOM node is reused).
      if (pre.dataset.enhanced === "true") return;
      pre.dataset.enhanced = "true";

      const code = pre.querySelector("code");
      const text = (code?.textContent ?? pre.textContent ?? "").trim();

      // Detect language from class — supports both `language-bash` and
      // `lang-bash` patterns CKEditor / Prism / hljs emit.
      const cls = code?.className || pre.className || "";
      const langMatch = /\b(?:language|lang)-([\w+-]+)/i.exec(cls);
      const lang = langMatch?.[1]?.toLowerCase() ?? "code";

      // Reset the prose style on the pre so our skin takes over fully.
      pre.classList.add(
        "!my-6",
        "!rounded-xl",
        "!border",
        "!border-black-800",
        "!bg-[#0d1117]",
        "!p-0",
        "!shadow-lg",
        "!overflow-hidden",
        "!whitespace-normal",
      );

      // Re-skin the inner code so the actual source is monospace +
      // light-on-dark, restores wrapping/scroll behavior pre's reset
      // removed, and adds breathing room.
      if (code) {
        code.classList.add(
          "block",
          "overflow-x-auto",
          "p-5",
          "font-mono",
          "text-[13px]",
          "leading-relaxed",
          "text-zinc-100",
        );
      } else {
        // No inner <code> (rare). Inline the source styling on the pre.
        pre.classList.add(
          "overflow-x-auto",
          "!p-5",
          "!font-mono",
          "!text-[13px]",
          "!leading-relaxed",
          "!text-zinc-100",
          "!whitespace-pre",
        );
      }

      // ---------- Header bar (language label + copy button) ----------
      const header = document.createElement("div");
      header.className =
        "flex items-center justify-between border-b border-black-800 bg-[#161b22] px-4 py-2";

      const langEl = document.createElement("span");
      langEl.className =
        "font-mono text-[11px] font-normal uppercase tracking-wider text-zinc-400";
      langEl.textContent = lang;

      const copyBtn = document.createElement("button");
      copyBtn.type = "button";
      copyBtn.className =
        "flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-normal text-zinc-400 transition-colors hover:bg-black-800 hover:text-white";
      copyBtn.setAttribute("aria-label", "Copy code to clipboard");
      copyBtn.innerHTML =
        '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg><span>Copy</span>';

      const onCopy = async () => {
        try {
          await navigator.clipboard.writeText(text);
          const label = copyBtn.querySelector("span");
          if (label) {
            const prev = label.textContent;
            label.textContent = "Copied";
            copyBtn.classList.add("!text-emerald-400");
            setTimeout(() => {
              label.textContent = prev;
              copyBtn.classList.remove("!text-emerald-400");
            }, 1500);
          }
        } catch {
          /* clipboard blocked — silent */
        }
      };
      copyBtn.addEventListener("click", onCopy);

      header.append(langEl, copyBtn);
      pre.prepend(header);

      disposers.push(() => copyBtn.removeEventListener("click", onCopy));
    });

    return () => disposers.forEach((d) => d());
  }, [details]);

  // Attach click handlers to every <img> in the article. Mark each one
  // with `cursor-zoom-in` so readers see it's interactive, then open the
  // lightbox on click. Re-runs whenever `details` changes so freshly-
  // hydrated CKEditor content gets the handlers too.
  useEffect(() => {
    if (!articleRef.current) return;
    const imgs = Array.from(
      articleRef.current.querySelectorAll<HTMLImageElement>("img"),
    );
    const handlers: Array<{ el: HTMLImageElement; fn: (e: Event) => void }> =
      [];
    imgs.forEach((img) => {
      img.style.cursor = "zoom-in";
      const fn = (e: Event) => {
        e.preventDefault();
        setLightboxImage({ src: img.src, alt: img.alt });
      };
      img.addEventListener("click", fn);
      handlers.push({ el: img, fn });
    });
    return () => {
      handlers.forEach(({ el, fn }) => el.removeEventListener("click", fn));
    };
  }, [details]);

  // Prefer the auto-extracted list (works for any content shape). Fall
  // back to the explicit prop if for some reason DOM scan finds nothing
  // (e.g. SSR snapshot before hydration).
  const tocItems =
    autoToc.length > 0
      ? autoToc
      : (tableOfContents ?? [])
          .filter((h): h is string => !!h)
          .map((h) => ({ id: slugify(h), text: h }));

  const hasToc = tocItems.length > 0;

  // Mobile-only collapsed-by-default state. On lg+ the content area is
  // always visible via `lg:!block`, regardless of state. We avoided <details>
  // because there's no responsive "default-open" attribute, so hiding the
  // <summary> on lg+ left the disclosure closed and the TOC invisible.
  const [tocOpen, setTocOpen] = useState(false);

  return (
    <section
      className={clsx(
        "container py-8 md:py-12 lg:py-16 xl:py-20",
        className,
      )}
      // Redefine the same design-token vars some blogs embed in their
      // CKEditor body (`:root { --sans: 'Poppins'; ... }`) at the
      // SECTION level — covers the TOC sidebar and author rail siblings,
      // not just the <article>. Children resolve var(--sans) from the
      // nearer ancestor → Bricolage everywhere in the reading column.
      style={
        {
          "--sans": "'Bricolage Grotesque', sans-serif",
          "--display": "'Bricolage Grotesque', sans-serif",
          "--mono": "'Bricolage Grotesque', sans-serif",
        } as React.CSSProperties
      }
      {...other}
    >
      {/* Three-column reading layout when both TOC and author bio exist:
            lg:  [200px TOC]  [article 1fr]                  (max-w 1280px)
            xl:  [200px TOC]  [article 1fr]  [240px author]  (max-w 1536px)
          1fr on the article column forces it to take all remaining space
          (≈1070px at lg, ≈1072px at xl). Bumped from 1152/1440 after a
          competitor sweep — Snyk and CrowdStrike both run 960px article
          columns inside a 1440px outer, and a content audit on the
          CERT-In post showed the 3-col VAPT cost table was wrapping
          its 91-char driver cells to 3 lines at the previous ~920px
          article width. The new widths relax that table without
          breaking layout, and the inline infographic max-width is
          lifted to 960px in the CKEditor source so the diagrams fill
          the wider column instead of leaving gutters.
          Below lg: stacks vertically. TOC accordion above article. */}
      <div
        className={clsx(
          "mx-auto flex max-w-3xl flex-col gap-8",
          hasToc &&
            "lg:grid lg:max-w-[1280px] lg:grid-cols-[200px_minmax(0,1fr)] lg:gap-x-10",
          hasToc &&
            author &&
            "xl:max-w-[1536px] xl:grid-cols-[200px_minmax(0,1fr)_240px] xl:gap-x-12",
        )}
      >
        {/* ---------- Sticky TOC sidebar ---------- */}
        {hasToc && (
          <aside className="lg:sticky lg:top-24 lg:h-[calc(100vh-7rem)] lg:self-start">
            <div className="rounded-xl border border-black-100 bg-white lg:border-0 lg:bg-transparent lg:p-0">
              {/* Mobile toggle — hidden on lg+ where TOC is always open */}
              <button
                type="button"
                onClick={() => setTocOpen((o) => !o)}
                aria-expanded={tocOpen}
                aria-controls="blog-toc-content"
                className="flex w-full cursor-pointer items-center justify-between px-5 py-4 font-bricolage-grotesque text-sm font-medium uppercase tracking-wider text-black-900 lg:hidden"
              >
                Table of Contents
                <svg
                  className={clsx(
                    "h-4 w-4 transition-transform",
                    tocOpen && "rotate-180",
                  )}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              <div
                id="blog-toc-content"
                className={clsx(
                  "border-t border-black-100 px-5 pb-5 pt-4 lg:!block lg:border-0 lg:p-0",
                  !tocOpen && "hidden",
                )}
              >
                <p className="mb-4 hidden font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-black-600 lg:block">
                  In this article
                </p>
                <ol className="space-y-2.5 text-sm">
                  {tocItems.map(({ id, text }) => (
                    <li key={id}>
                      <a
                        href={`#${id}`}
                        onClick={(e) => {
                          e.preventDefault();
                          scrollToHeading(id);
                          setTocOpen(false);
                        }}
                        className={clsx(
                          "block cursor-pointer border-l-2 pl-3 font-normal transition-colors",
                          activeId === id
                            ? "border-l-yellow-400 font-medium text-secondary-900"
                            : "border-transparent text-black-600 hover:text-secondary-900",
                        )}
                      >
                        {text}
                      </a>
                    </li>
                  ))}
                </ol>

                {/* Share row in the sidebar (desktop only). Mobile share
                    sits at the bottom of the article as before. */}
                <div className="mt-8 hidden border-t border-black-100 pt-6 lg:block">
                  <p className="mb-3 font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-black-600">
                    Share
                  </p>
                  <BlogSocialMediaHandles className="flex flex-wrap gap-3" />
                </div>
              </div>
            </div>
          </aside>
        )}

        {/* ---------- Article body ----------
            suppressHydrationWarning: CKEditor source HTML is re-parsed
            by html-react-parser during hydration; whitespace text nodes
            occasionally normalize differently between server and client.
            CSS variable override now lives on the outer <section> so it
            also covers the TOC sidebar and author rail. */}
        <article
          ref={articleRef}
          // Defensive responsive guardrails for everything CKEditor / Strapi
          // hands us:
          //   - [&_img] caps every inline image at the article column width
          //     and preserves aspect ratio, so a 1920px screenshot can't
          //     blow out the layout at 320px viewport
          //   - [&_figure] block-aligns figures with sensible margins
          //   - [&_figure.table] (CKEditor's <figure class="table">) gets
          //     the overflow-x:auto so the FIGURE scrolls horizontally on
          //     mobile. Putting the rule on <table> instead (the old
          //     v0.11.84 approach) forces display:block on the table which
          //     kills table-layout AND collapses the figure to the table's
          //     natural intrinsic width, so max-w:100% on the table clamps
          //     to nothing and overflow never kicks in. The figure is the
          //     actual block-level element overflowing the column, so
          //     that's where the scroll container belongs.
          className="min-w-0 [&_figure]:my-6 [&_img]:block [&_img]:h-auto [&_img]:max-w-full [&_figure.table]:block [&_figure.table]:max-w-full [&_figure.table]:overflow-x-auto"
          suppressHydrationWarning
        >
          {details.map(({ heading, description, tabs }, idx) => (
            <section
              key={heading ?? `section-${idx}`}
              className="mt-10 first:mt-0 md:mt-12 lg:mt-14"
            >
              {heading && (
                <h2
                  id={slugify(heading)}
                  // Matches the inner h2 clamp inside HtmlText below so a
                  // structured-section blog and a monolithic-body blog
                  // both render h2s at the same size.
                  className="mb-3 scroll-mt-24 font-bricolage-grotesque text-[clamp(1.25rem,1vw+0.875rem,1.625rem)] font-medium !leading-snug text-black-900"
                >
                  {heading}
                </h2>
              )}
              {description && (
                <HtmlText
                  className={clsx(
                    "prose-strong:!inline ck-content",
                    // Unify the article body font with the rest of the
                    // site — Bricolage Grotesque is what headings, card
                    // titles, and editorial chrome use.
                    "font-bricolage-grotesque",
                    // Single body size for ALL text-level elements
                    // (paragraphs, lists, blockquotes, table cells,
                    // captions). Previously p/li had a clamp but
                    // blockquote, code, td, etc. fell back to HtmlText's
                    // larger defaults — same blog read at three or four
                    // different sizes. Now everything that's not a
                    // heading uses the same 15→17px clamp.
                    "[&_p]:!font-bricolage-grotesque [&_p]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_p]:!leading-[1.75]",
                    "[&_li]:!font-bricolage-grotesque [&_li]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_li]:!leading-[1.7]",
                    "[&_blockquote]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_blockquote_*]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)]",
                    "[&_td]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_th]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)]",
                    "[&_figcaption]:!text-[clamp(0.8125rem,0.2vw+0.75rem,0.9375rem)]",
                    "[&_strong]:!text-inherit [&_em]:!text-inherit [&_a]:!text-inherit",
                    // Heading scale — predictable, smaller than HtmlText's
                    // article-page defaults (which were calibrated for
                    // hero/landing-page rendering, not body prose). H2/H3
                    // here are now tight enough that the body reads as
                    // one continuous system, with hierarchy via weight +
                    // spacing rather than dramatic size shifts.
                    "[&_h2]:!text-[clamp(1.25rem,1vw+0.875rem,1.625rem)] [&_h2]:!font-medium [&_h2]:!leading-snug [&_h2]:!mb-3 [&_h2]:!mt-10",
                    "[&_h3]:!text-[clamp(1.125rem,0.75vw+0.875rem,1.375rem)] [&_h3]:!font-medium [&_h3]:!leading-snug [&_h3]:!mb-2 [&_h3]:!mt-8",
                    "[&_h4]:!text-[clamp(1.0625rem,0.5vw+0.875rem,1.25rem)] [&_h4]:!font-medium [&_h4]:!leading-snug [&_h4]:!mb-2 [&_h4]:!mt-6",
                    "[&_h5]:!text-[clamp(1rem,0.25vw+0.9375rem,1.125rem)] [&_h5]:!font-medium [&_h5]:!leading-snug",
                    "[&_h6]:!text-[clamp(0.9375rem,0.25vw+0.875rem,1.0625rem)] [&_h6]:!font-medium [&_h6]:!leading-snug",
                    // scroll-mt offsets the smooth-scroll landing point
                    // below the fixed header so the heading isn't tucked
                    // under it. Applies to inline h2/h3s in the CKEditor
                    // body for blogs authored as a single monolithic block.
                    "[&_h2]:scroll-mt-24 [&_h3]:scroll-mt-24",
                  )}
                >
                  <CKEditorHtmlContent htmlContent={description} />
                </HtmlText>
              )}
              {tabs && tabs.length > 0 && (
                <TwoColumnTab tabs={tabs} className="mt-6" />
              )}
            </section>
          ))}

          {/* Author bio falls here at lg (where the right rail is not yet
              shown) and below lg. On xl+, the right-rail <aside> below
              takes over and this inline copy is hidden. */}
          {author && (
            <BlogAuthorCardSection
              image={author.image}
              name={author.name}
              designation={author.designation}
              description={author.description}
              socialHandles={author.socialHandles}
              className={clsx(
                "mt-12 lg:mt-16",
                hasToc && author && "xl:hidden",
              )}
            />
          )}

          {/* Share — inline below article on mobile, AND on desktop when
              the post has no TOC (because the desktop share lives inside
              the TOC sidebar). With a TOC present, hide on lg+ to avoid
              duplicating it. */}
          <div
            className={clsx(
              "mt-8 flex flex-row items-center gap-4",
              hasToc && "lg:hidden",
            )}
          >
            <p className="font-bricolage-grotesque text-xs font-medium uppercase tracking-wider text-black-600">
              Share
            </p>
            <BlogSocialMediaHandles />
          </div>
        </article>

        {/* ---------- Right rail: sticky author bio at xl+ ----------
            Only renders when both TOC and author exist (3-col layout).
            Below xl, the inline author card inside the article column
            handles this. */}
        {hasToc && author && (
          <aside className="hidden xl:block xl:sticky xl:top-24 xl:h-[calc(100vh-7rem)] xl:self-start xl:overflow-y-auto">
            <BlogAuthorCardSection
              image={author.image}
              name={author.name}
              designation={author.designation}
              description={author.description}
              socialHandles={author.socialHandles}
              compact
            />
          </aside>
        )}
      </div>

      {/* Click-to-zoom lightbox. Portaled to <body> so the overlay
          isn't clipped by parent overflow / sticky-stacking contexts. */}
      <BlogImageLightbox
        open={lightboxImage}
        onClose={() => setLightboxImage(null)}
      />
    </section>
  );
};

export default BlogDetailHtmlText;
